/**
 * Populates the "Real Results" reviews carousel with live reviews from
 * BayEngage's storefront API (read-only GET, all products, most recent first).
 * The section is marked with `data-bayengage-reviews`; the static review blocks
 * stay as a fallback when the store id can't be resolved or the fetch fails.
 */
(() => {
  const section = document.querySelector('[data-bayengage-reviews]');
  const track = section && section.querySelector('[data-track]');
  if (!track) return;

  const API = 'https://api-rvsf.bayengage.com/api/v1';
  const count = Math.max(1, parseInt(section.dataset.count, 10) || 8);

  // The store id lives in the BayEngage loader URL (…/sf.js?t=<id>); fall back
  // to the known tenant id if the loader script isn't on the page.
  const loader = document.querySelector('script[src*="sf.bayengage.com"]');
  const storeId = (loader && loader.src.match(/[?&]t=([^&]+)/)?.[1]) || '1d66393cbc94';

  const escape = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);

  const avatar =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">' +
    '<path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.2-8 5v1h16v-1c0-2.8-3.6-5-8-5Z"/></svg>';

  const cardHTML = (review) => {
    const name = escape(review.user?.display_name || review.user?.name || 'Verified Buyer');
    const percent = Math.round((Number(review.score) || 0) * 20);
    const verified = review.verified ? '<p class="ws-reviews__verified">Verified Buyer</p>' : '';
    const title = review.title ? `<h3 class="ws-reviews__card-title">${escape(review.title)}</h3>` : '';
    const text = escape((review.content || '').slice(0, 260));

    const product = review.reviewed_product || {};
    let productLink = '';
    if (product.title) {
      // Strip any origin, then only trust a single-slash same-origin path;
      // anything else (javascript:, data:, //host, relative) falls back to '#'.
      const path = (product.product_url || '').replace(/^https?:\/\/[^/]+/, '');
      const href = escape(/^\/(?!\/)/.test(path) ? path : '#');
      const image = product.product_image
        ? `<img class="ws-reviews__card-product-image" src="${escape(product.product_image)}" alt="${escape(product.title)}" loading="lazy">`
        : '';
      productLink =
        `<a class="ws-reviews__card-product" href="${href}">${image}` +
        `<span class="ws-reviews__card-product-title">${escape(product.title)}</span></a>`;
    }

    return `<div class="ws-reviews__card">
      <div class="ws-reviews__card-top">
        <div class="ws-reviews__card-head">
          <span class="ws-reviews__avatar" aria-hidden="true">${avatar}</span>
          <div class="ws-reviews__name-date"><p class="ws-reviews__name">${name}</p>${verified}</div>
        </div>
        <div class="ws-reviews__stars ws-reviews__stars--card" style="--rating-percent: ${percent}%" aria-hidden="true">
          <span class="ws-reviews__stars-row">★★★★★</span>
          <span class="ws-reviews__stars-row ws-reviews__stars-row--filled">★★★★★</span>
        </div>
        <span class="visually-hidden">Rated ${Number(review.score) || 0} out of 5</span>
        ${title}
        <p class="ws-reviews__card-text">${text}</p>
      </div>
      ${productLink}
    </div>`;
  };

  const isGuest = (name) => !name || /^guest$/i.test(name) || /^anonymous$/i.test(name);

  // The section starts with `data-loading` so the static fallback cards stay
  // hidden; revealing swaps in the dynamic cards (or the fallback) without a
  // flash of the placeholder content.
  const reveal = () => section.removeAttribute('data-loading');

  const load = async () => {
    try {
      // Fetch several pages concurrently, then keep only high-rated, named
      // reviews and show a random selection so the section varies each visit.
      const pages = await Promise.all(
        [1, 2, 3, 4, 5].map((page) =>
          fetch(`${API}/product/reviews/product/all/${page}/recent`, {
            headers: { 'store-id': storeId },
          })
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        )
      );

      const pool = [];
      for (const json of pages) {
        for (const review of json?.data?.reviews?.data || []) {
          const name = (review.user?.display_name || review.user?.name || '').trim();
          if (review.content && Number(review.score) >= 4 && !isGuest(name)) pool.push(review);
        }
      }
      if (!pool.length) return; // keep the static fallback cards

      // Fisher–Yates shuffle.
      for (let i = pool.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }

      track.innerHTML = pool.slice(0, count).map(cardHTML).join('');
      track.dispatchEvent(new Event('scroll')); // refresh carousel arrow state
    } catch (error) {
      /* network/parse failure — leave the static review blocks in place */
    } finally {
      reveal();
    }
  };

  load();
})();
